import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swiper, { Navigation } from 'swiper';
import Highlighter, { HighlighterItem } from './highlighter';
import Particles from './particles';

// Import Swiper
import 'swiper/swiper.min.css';
import {
	BeakerIcon,
	CalendarIcon,
	HomeIcon,
} from '@heroicons/react/24/outline';
import { CardiogramEOutline } from 'healthicons-react';
Swiper.use([Navigation]);

export default function TestimonialsCarousel() {
	const { t } = useTranslation();
	const [swiperInitialized, setSwiperInitialized] = useState<boolean>(false);

	const items = [
		{
			name: t('testimonials.item1.name'),
			description: t('testimonials.item1.description'),
			icon: <HomeIcon className="h-8 w-8 text-teal-500" />,
		},
		{
			name: t('testimonials.item2.name'),
			description: t('testimonials.item2.description'),
			icon: <CalendarIcon className="h-8 w-8 text-teal-500" />,
		},
		{
			name: t('testimonials.item3.name'),
			description: t('testimonials.item3.description'),
			icon: <BeakerIcon className="h-8 w-8 text-teal-500" />,
		},
		{
			name: t('testimonials.item4.name'),
			description: t('testimonials.item4.description'),
			icon: <CardiogramEOutline className="h-8 w-8 text-teal-500" />,
		},
	];

	useEffect(() => {
		const carousel = new Swiper('.stellar-carousel', {
			breakpoints: {
				320: {
					slidesPerView: 1,
				},
				640: {
					slidesPerView: 2,
				},
				1024: {
					slidesPerView: 3,
				},
			},
			grabCursor: true,
			loop: false,
			centeredSlides: false,
			initialSlide: 0,
			spaceBetween: 24,
			navigation: {
				nextEl: '.carousel-next',
				prevEl: '.carousel-prev',
			},
		});
		setSwiperInitialized(true);
	}, []);

	return (
		<section>
			<div className="mx-auto max-w-6xl px-4 sm:px-6">
				<div className="pt-12 md:pt-20">
					{/* Section header */}
					<div className="mx-auto max-w-3xl pb-12 text-center md:pb-20">
						<h2 className="h2 bg-gradient-to-r from-slate-200/60 via-slate-200 to-slate-200/60 bg-clip-text pb-4 text-transparent">
							{t('testimonials.sectionHeader.title')}
						</h2>
						<p className="text-lg text-slate-400">
							{t('testimonials.sectionHeader.description')}
						</p>
					</div>

					{/* Carousel built with Swiper.js [https://swiperjs.com/] */}
					{/* * Custom styles in src/css/additional-styles/theme.scss */}
					<div className="relative before:absolute before:inset-0 before:z-20 before:-translate-x-full before:bg-gradient-to-l before:from-transparent before:to-slate-900 before:to-20% after:absolute after:inset-0 after:z-20 after:translate-x-full after:bg-gradient-to-r after:from-transparent after:to-slate-900 after:to-20%">
						<div className="stellar-carousel swiper-container group">
							<Highlighter
								className="swiper-wrapper w-fit"
								refresh={swiperInitialized}
							>
								{items.map((item, index) => (
									<HighlighterItem
										className="swiper-slide group/slide h-auto"
										key={index}
									>
										<div className="relative z-20 h-full overflow-hidden rounded-[inherit] bg-slate-900">
											{/* Particles animation */}
											<Particles
												className="absolute inset-0 -z-10 opacity-0 transition-opacity duration-500 ease-in-out group-hover/slide:opacity-100 group-[.swiper-slide-active]/slide:opacity-100"
												quantity={3}
												refresh={swiperInitialized}
											/>
											{/* Radial gradient */}
											<div
												className="pointer-events-none absolute bottom-0 left-1/2 -z-10 aspect-square w-1/3 -translate-x-1/2 translate-y-1/2"
												aria-hidden="true"
											>
												<div className="translate-z-0 absolute inset-0 rounded-full bg-slate-800 blur-[60px] transition-colors duration-500 ease-in-out group-[.swiper-slide-active]/slide:bg-teal-500" />
											</div>
											<div className="flex h-full flex-col p-6">
												<div className="mb-4 w-fit rounded-full border border-slate-600 bg-slate-900 p-2">
													{item.icon}
												</div>
												<div className="grow">
													<div className="mb-1 text-lg font-bold">
														{item.name}
													</div>
													<div className="mb-3 text-slate-400">
														{item.description}
													</div>
												</div>
											</div>
										</div>
									</HighlighterItem>
								))}
							</Highlighter>
						</div>
					</div>

					{/* Arrows */}
					<div className="mt-8 flex justify-end">
						<button className="carousel-prev group relative z-20 flex h-12 w-12 items-center justify-center">
							<span className="sr-only">Previous</span>
							<svg
								className="h-4 w-4 fill-slate-500 transition duration-150 ease-in-out group-hover:fill-teal-500"
								viewBox="0 0 16 16"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="M6.7 14.7l1.4-1.4L3.8 9H16V7H3.8l4.3-4.3-1.4-1.4L0 8z" />
							</svg>
						</button>
						<button className="carousel-next group relative z-20 flex h-12 w-12 items-center justify-center">
							<span className="sr-only">Next</span>
							<svg
								className="h-4 w-4 fill-slate-500 transition duration-150 ease-in-out group-hover:fill-teal-500"
								viewBox="0 0 16 16"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="M9.3 14.7l-1.4-1.4L12.2 9H0V7h12.2L7.9 2.7l1.4-1.4L16 8z" />
							</svg>
						</button>
					</div>
				</div>
			</div>
		</section>
	);
}
